import { useFSM } from '@fsmify/vue'
import { isUndefined } from 'ramda-adjunct'
import { type ComputedRef, onBeforeMount, readonly, type Ref, ref, unref, watch } from 'vue'

import { useHardwareCheck } from '../../shared/composables/useHardwareCheck'

export type UseUserMediaConstraints = {
  video: {
    facingMode: 'user' | 'environment'
    deviceId: string
  }
  audio: { enabled: true; deviceId: string }
}

const getMediaStream = async ({
  video,
  audio,
}: UseUserMediaConstraints): Promise<MediaStream | undefined> => {
  try {
    return await globalThis.navigator.mediaDevices.getUserMedia({
      audio,
      video,
    })
  } catch {
    return undefined
  }
}

export const useUserMedia = (
  constraints: Ref<UseUserMediaConstraints> | ComputedRef<UseUserMediaConstraints>,
): {
  stream: Readonly<Ref<MediaStream | undefined>>
  state: typeof fsm.currentState
} => {
  const fsm = useFSM({
    initialState: 'init',
    states: {
      init: {
        REQUEST_PERMISSION: 'permissionPending',
      },
      permissionPending: {
        STREAM_READY: 'initialised',
        PERMISSION_DENY: 'permissionDenied',
      },
      retrievingStream: { STREAM_RETRIEVED: 'initialised' },
      initialised: {},
      permissionDenied: {},
      devicesMissing: {
        DEVICE_FOUND: 'permissionPending',
      },
    },
    globalEvents: {
      DEVICES_NOT_FOUND: 'devicesMissing',
    },
  })
  onBeforeMount(() => {
    fsm.send('REQUEST_PERMISSION')
  })
  const stream = ref<MediaStream | undefined>()

  const updateStream = async (): Promise<void> => {
    stream.value = await getMediaStream(unref(constraints))
  }
  watch(
    constraints,
    () => {
      updateStream()
    },
    { deep: true },
  )

  fsm.onAfterEnterState('permissionPending', () => {
    // using async iife so that fsm.send is not blocked
    ;(async () => {
      await updateStream()
      fsm.send(isUndefined(stream.value) ? 'PERMISSION_DENY' : 'STREAM_READY')
    })()
  })

  const { hasMic, hasWebcam } = useHardwareCheck()
  watch(
    () => hasMic.value && hasWebcam.value,
    (hasMediaDevices) => {
      if (!hasMediaDevices) {
        stream.value = undefined
        fsm.send('DEVICES_NOT_FOUND')
        return
      }
      fsm.send('DEVICE_FOUND')
    },
    { immediate: true },
  )

  return {
    stream: readonly(stream),
    state: fsm.currentState,
  }
}
